import { inject, Injectable } from '@angular/core';

import moment from 'moment-timezone';

import { I18nService } from '@core/i18n-legacy';
import { getFormattedTimezone } from '@core/time-legacy';

@Injectable({
  providedIn: 'root',
})
export class AutomationsSendAfterService {
  private readonly i18n = inject(I18nService);

  public twoDigits(number: number): string {
    return number < 10 ? '0' + number : number.toString();
  }

  public timezoneText(timezone: string | null): string {
    return timezone ? getFormattedTimezone(timezone) : '';
  }

  public calculateUTCTime(
    days: number | null,
    hour: number | null,
    minute: number | null,
    timezone: string | null,
  ): {
    sendAfterDays: number | null;
    sendAfterAtHour: number | null;
    sendAfterAtMinute: number | null;
  } {
    if (this.invalidSendAfterData(days, hour, minute, timezone)) {
      return { sendAfterDays: days, sendAfterAtHour: hour, sendAfterAtMinute: minute };
    }

    // Create moment object in target timezone, set desired hour and minutes, convert it to UTC and read hours and minutes
    const date = moment().tz(timezone!).startOf('day').hours(hour!).minutes(minute!).utc();

    return {
      sendAfterDays: days,
      sendAfterAtHour: date.hour(),
      sendAfterAtMinute: date.minute(),
    };
  }

  public calculateFromUTCTime(
    utcDays: number | null,
    utcHour: number | null,
    utcMinute: number | null,
    timezone: string | null,
  ): {
    sendAfterDays: number | null;
    sendAfterAtHour: number | null;
    sendAfterAtMinute: number | null;
  } {
    if (this.invalidSendAfterData(utcDays, utcHour, utcMinute, timezone)) {
      return { sendAfterDays: utcDays, sendAfterAtHour: utcHour, sendAfterAtMinute: utcMinute };
    }

    // Create moment object in UTC, set desired hour and minutes, convert it to target timezone and read hours and minutes
    const date = moment().utc().hours(utcHour!).minutes(utcMinute!).tz(timezone!);

    return {
      sendAfterDays: utcDays,
      sendAfterAtHour: date.hour(),
      sendAfterAtMinute: date.minute(),
    };
  }

  public sendLaterData(data: {
    sendAfterDays?: number | null;
    sendAfterAtHour?: number | null;
    sendAfterAtMinute?: number | null;
    sendAtTimezone?: string | null;
  }): {
    sendAfterDays?: number | null;
    sendAfterAtHour?: number | null;
    sendAfterAtMinute?: number | null;
    sendAfterTimezone?: string | null;
  } {
    if (
      this.invalidSendAfterData(
        data.sendAfterDays,
        data.sendAfterAtHour,
        data.sendAfterAtMinute,
        data.sendAtTimezone,
      )
    )
      return {};
    const { sendAfterDays, sendAfterAtHour, sendAfterAtMinute } = this.calculateFromUTCTime(
      data.sendAfterDays!,
      data.sendAfterAtHour!,
      data.sendAfterAtMinute!,
      data.sendAtTimezone!,
    );
    return {
      sendAfterDays,
      sendAfterAtHour,
      sendAfterAtMinute,
      ...(data.sendAtTimezone
        ? {
            sendAfterTimezone: data.sendAtTimezone,
            timezoneText: this.timezoneText(data.sendAtTimezone),
          }
        : {}),
    };
  }

  public setSendAtText(days: number | null, hour: number | null, minute: number | null): string {
    if (days === null || hour === null || minute === null) {
      return this.i18n.translate('app.mailbox.send_immediately');
    } else if (days === 0) {
      return `${this.i18n.translate('app.mailbox.send_after_same_day')}
        (${this.twoDigits(hour)}:${this.twoDigits(minute)})`;
    } else {
      return `${days} ${this.i18n.translate('app.mailbox.days_after')}
        (${this.twoDigits(hour)}:${this.twoDigits(minute)})`;
    }
  }

  public invalidSendAfterData(
    days: number | null | undefined,
    hour: number | null | undefined,
    minute: number | null | undefined,
    timezone: string | null | undefined,
  ): boolean {
    return (
      days === null ||
      days === undefined ||
      !Number.isInteger(days) ||
      hour === null ||
      hour === undefined ||
      minute === null ||
      minute === undefined ||
      !timezone
    );
  }
}
